import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';

const NAV_ITEMS = [
  { label: '首页', path: '/' },
  { label: '影视分类', path: '/category' },
  { label: '影视资讯', path: '/news' },
  { label: '关于我们', path: '/about' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setFocused(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return { movies: [], news: [] };
    return {
      movies: movies.filter((m) =>
        [m.title, m.director, ...m.cast, ...m.genres].join(' ').includes(kw)
      ).slice(0, 5),
      news: news.filter((n) => (n.title + n.summary).includes(kw)).slice(0, 4),
    };
  }, [keyword]);

  const hasResults = results.movies.length > 0 || results.news.length > 0;

  const goSearch = () => {
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/category?keyword=${encodeURIComponent(kw)}`);
    setFocused(false);
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-xl font-bold text-foreground-950">
            奸
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">奸情影视大全</span>
            <span className="font-label mt-0.5 text-[10px] uppercase tracking-[0.3em] text-primary-400">
              Drama Portal
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-primary-500/15 text-primary-300'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div ref={searchRef} className="relative ml-auto hidden w-72 md:block lg:w-80">
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-500">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onFocus={() => setFocused(true)}
              onKeyDown={(e) => e.key === 'Enter' && goSearch()}
              placeholder="搜索影视作品或资讯"
              className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>

          {focused && keyword.trim() && (
            <div className="absolute left-0 right-0 top-full mt-2 max-h-96 overflow-y-auto rounded-lg border border-background-200 bg-background-100 p-2 shadow-lg">
              {!hasResults && (
                <p className="px-3 py-6 text-center text-sm text-foreground-600">
                  没有找到与“{keyword}”相关的内容
                </p>
              )}
              {results.movies.length > 0 && (
                <div className="mb-1">
                  <p className="px-3 py-1.5 text-xs font-semibold tracking-wide text-foreground-500">影视作品</p>
                  {results.movies.map((m) => (
                    <Link
                      key={m.id}
                      to={`/detail/${m.id}`}
                      className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-background-200"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded bg-primary-500/15 text-primary-300">
                        <i className="ri-film-line"></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground-950">{m.title}</span>
                        <span className="block truncate text-xs text-foreground-500">
                          {m.category} · {m.year} · {m.region}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
              {results.news.length > 0 && (
                <div>
                  <p className="px-3 py-1.5 text-xs font-semibold tracking-wide text-foreground-500">资讯</p>
                  {results.news.map((n) => (
                    <Link
                      key={n.id}
                      to={`/news/detail/${n.id}`}
                      className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-background-200"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded bg-accent-500/15 text-accent-400">
                        <i className="ri-newspaper-line"></i>
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-950">{n.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-950 lg:hidden"
          aria-label="打开菜单"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="mb-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && goSearch()}
              placeholder="搜索影视作品或资讯"
              className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`rounded-md px-4 py-3 text-sm font-medium ${
                  isActive(item.path)
                    ? 'bg-primary-500/15 text-primary-300'
                    : 'text-foreground-800 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}